import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Eye, Heart, CalendarClock } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useSiteConfig } from "../context/ConfigContext";

function StreamCard({ s, live }) {
  return (
    <Link
      data-testid={`stream-card-${s.id}`}
      to={`/live/${s.id}`}
      className="group relative block overflow-hidden rounded-2xl aspect-[4/3] bg-teal-950 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
    >
      {s.thumbnail && (
        <img src={mediaUrl(s.thumbnail)} alt={s.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-500 group-hover:scale-105" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
      {live ? (
        <span className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
          <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> Live
        </span>
      ) : (
        <span className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-teal-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
          <CalendarClock className="h-3 w-3" /> Upcoming
        </span>
      )}
      <div className="absolute bottom-0 inset-x-0 p-4">
        <p className="font-heading font-bold text-white truncate">{s.title}</p>
        <p className="text-xs text-teal-200 truncate">{s.supplier_name || s.host_name}</p>
        <div className="mt-1.5 flex items-center gap-3 text-[11px] text-white/70">
          <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {s.viewers ?? 0}</span>
          <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {s.likes ?? 0}</span>
          {!live && s.scheduled_at && <span>{new Date(s.scheduled_at).toLocaleString()}</span>}
        </div>
      </div>
    </Link>
  );
}

export default function Live() {
  const { t } = useLang();
  const { features } = useSiteConfig();
  const [live, setLive] = useState(null);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    api("/home")
      .then((h) => {
        setLive((h.live || []).filter((s) => s.status === "live"));
        setUpcoming(h.upcoming || []);
      })
      .catch(() => setLive([]));
  }, []);

  if (features.live === false)
    return (
      <div data-testid="live-off" className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="font-heading text-xl font-semibold text-slate-800">Live shopping</p>
        <p className="mt-2 text-sm text-slate-500">{t("no_live")}</p>
      </div>
    );

  return (
    <div data-testid="live-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-600/10 text-red-600">
          <Radio className="h-6 w-6" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">{t("live_t")}</h1>
          <p className="text-sm text-slate-600">{t("live_sub")}</p>
        </div>
      </div>

      <h2 className="font-heading mt-10 mb-5 text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full bg-red-600 animate-pulse" /> {t("live_now")}
      </h2>
      {live === null ? (
        <p data-testid="live-loading" className="text-sm text-slate-500">...</p>
      ) : live.length === 0 ? (
        <div data-testid="live-empty" className="rounded-2xl border border-dashed border-slate-300 py-12 text-center">
          <p className="font-heading font-semibold text-slate-800">{t("no_live_t")}</p>
          <p className="mt-1 text-sm text-slate-500">{t("no_live_sub")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {live.map((s) => (
            <StreamCard key={s.id} s={s} live />
          ))}
        </div>
      )}

      {upcoming.length > 0 && (
        <>
          <h2 className="font-heading mt-12 mb-5 text-xl sm:text-2xl font-semibold text-slate-900">{t("upcoming")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {upcoming.map((s) => (
              <StreamCard key={s.id} s={s} live={false} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
